import { Suspense } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '@stores/index'
import { LoadingSpinner } from '@components/ui'
import { usePermission } from '@/hooks/usePermission'
import { NAV_ITEMS, DEFAULT_ROUTES } from '@/config/permissions'
import type { Resource, Action } from '@/config/permissions'

interface ProtectedRouteProps {
  requiredResource?: Resource
  requiredAction?: Action
}

export function ProtectedRoute({ requiredResource, requiredAction }: ProtectedRouteProps = {}) {
  const { isAuthenticated } = useAuthStore()
  const { canAccess, role } = usePermission()
  const location = useLocation()

  // Not authenticated -> login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // Get required permission from props or path matching
  const getRequiredPermission = (): { resource: Resource; action: Action } | null => {
    if (requiredResource && requiredAction) {
      return { resource: requiredResource, action: requiredAction }
    }

    // Match current path to NAV_ITEMS
    const currentPath = location.pathname
    const navItem = NAV_ITEMS.find(
      (item) => currentPath === item.path || currentPath.startsWith(`${item.path}/`)
    )

    return navItem ? { resource: navItem.resource, action: navItem.action } : null
  }

  const permission = getRequiredPermission()

  // Check permission if route requires one
  // Uses canAccess() which returns true for both full and :own permissions
  // Data-level filtering is handled by the API/backend using ownership checks
  if (permission && !canAccess(permission.resource, permission.action)) {
    // Redirect to role's default route
    const defaultRoute = DEFAULT_ROUTES[role] || '/dashboard'
    return <Navigate to={defaultRoute} replace />
  }

  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-screen bg-slate-950">
          <LoadingSpinner size="lg" className="text-sky-400" />
        </div>
      }
    >
      <Outlet />
    </Suspense>
  )
}
